import { useMemo } from 'react';
import { Button } from 'components/ui/button';
import type { Artist } from 'types';
import { MapPin, Music, Users, X } from 'lucide-react';
import { ExpandableMultiFilter } from './expandable-multi-filter';
import { DistanceFilter } from './distance-filter';
import { MonthlyListenersFilter } from './monthly-listeners-filter';
import type { ArtistMultiFilters, MultiFilterKey } from './multi-filter-types';
import { useTranslation } from 'react-i18next';

interface ArtistMultiFiltersProps {
  artists?: Artist[];
  filters: ArtistMultiFilters;
  onFilterChange: (key: MultiFilterKey, values: string[]) => void;
  onDistanceChange: (distance: string) => void;
  onMonthlyListenersChange: (bucket: string) => void;
  onClearFilters: () => void;
  onClose: () => void;
  userLocation: { lat: number; lng: number } | null;
  onLocationUpdate: (location: { lat: number; lng: number } | null) => void;
  facets?: {
    genres: Array<{ value: string; count: number }>;
    skills: Array<{ value: string; count: number }>;
    countries?: Array<{ value: string; count: number }>;
    monthlyListenersBuckets: Array<{
      bucket: 'under-100k' | '100k-500k' | '500k-1m' | 'over-1m';
      count: number;
    }>;
    distanceBuckets?: Array<{
      bucket: 'within-1000' | 'within-5000';
      count: number;
    }>;
  };
}

export function ArtistMultiFilters({
  artists = [],
  filters,
  onFilterChange,
  onDistanceChange,
  onMonthlyListenersChange,
  onClearFilters,
  onClose,
  userLocation,
  onLocationUpdate,
  facets,
}: ArtistMultiFiltersProps) {
  const { t } = useTranslation('collab-club');

  // Use facets for options if available, otherwise fall back to extracting from artists
  const genres = facets?.genres
    ? ['all', ...facets.genres.map((f) => f.value)]
    : [
        'all',
        ...Array.from(new Set(artists.flatMap((a) => a.genres).filter(Boolean) as string[])).sort(),
      ];

  const skills = facets?.skills
    ? ['all', ...facets.skills.map((f) => f.value)]
    : [
        'all',
        ...Array.from(new Set(artists.flatMap((a) => a.skills).filter(Boolean) as string[])).sort(),
      ];

  const countries = ['all', ...(facets?.countries?.map((f) => f.value) ?? [])];

  // Build distance options from facets
  const distances = useMemo(() => {
    // Always include "all" option
    const options = [{ value: 'all', label: t('all_locations') }];

    // If no facets available, return just the "all" option
    if (!facets?.distanceBuckets || facets.distanceBuckets.length === 0) {
      return options;
    }

    // Add options based on available distance buckets from API
    facets.distanceBuckets.forEach((bucket) => {
      // Use the bucket name as the value and create a label
      // The API should provide consistent bucket names
      options.push({
        value: bucket.bucket,
        label: t(`distance_${bucket.bucket}`), // Use translation key based on bucket name
      });
    });

    return options;
  }, [facets?.distanceBuckets, t]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* Genre Multi-Filter */}
        <ExpandableMultiFilter
          title={t('genre')}
          icon={<Music className="h-4 w-4" />}
          searchable
          options={genres}
          selectedValues={filters.genres}
          onSelectionChange={(values) => onFilterChange('genres', values)}
          initialLimit={11}
          showCounts={true}
          hideEmptyOptions={true}
          facetCounts={
            facets?.genres
              ? (facets.genres.reduce((acc, f) => ({ ...acc, [f.value]: f.count }), {}) as Record<
                  string,
                  number
                >)
              : undefined
          }
        />

        {/* Distance + Country stacked in one column */}
        <div className="space-y-8">
          {/* Distance Filter (single selection) */}
          <DistanceFilter
            title={t('distance')}
            options={distances}
            selectedValue={filters.distance}
            onSelectionChange={onDistanceChange}
            userLocation={userLocation}
            onLocationUpdate={onLocationUpdate}
            facetCounts={
              facets?.distanceBuckets
                ? facets.distanceBuckets.reduce(
                    (acc, bucket) => ({
                      ...acc,
                      [bucket.bucket]: bucket.count,
                    }),
                    {} as Record<string, number>
                  )
                : undefined
            }
          />

          {/* Country Multi-Filter */}
          <ExpandableMultiFilter
            title={t('countries')}
            icon={<MapPin className="h-4 w-4" />}
            searchable
            options={countries}
            selectedValues={filters.countries}
            onSelectionChange={(values) => onFilterChange('countries', values)}
            initialLimit={6}
            showCounts={true}
            hideEmptyOptions={true}
            facetCounts={
              facets?.countries
                ? (facets.countries.reduce(
                    (acc, f) => ({ ...acc, [f.value]: f.count }),
                    {}
                  ) as Record<string, number>)
                : undefined
            }
          />
        </div>

        {/* Monthly Listeners Filter */}
        <MonthlyListenersFilter
          value={filters.monthlyListenersBucket}
          onChange={onMonthlyListenersChange}
          facets={facets?.monthlyListenersBuckets}
        />

        {/* Skills Multi-Filter */}
        <ExpandableMultiFilter
          title={t('skills')}
          icon={<Users className="h-4 w-4" />}
          searchable
          options={skills}
          selectedValues={filters.skills}
          onSelectionChange={(values) => onFilterChange('skills', values)}
          initialLimit={11}
          showCounts={true}
          hideEmptyOptions={true}
          facetCounts={
            facets?.skills
              ? (facets.skills.reduce((acc, f) => ({ ...acc, [f.value]: f.count }), {}) as Record<
                  string,
                  number
                >)
              : undefined
          }
        />
      </div>

      {/* Filter Actions */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={onClearFilters}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
        >
          <X className="h-4 w-4" />
          {t('clear_all')}
        </button>
        <Button
          onClick={onClose}
          className="bg-gray-900 hover:bg-gray-800 text-white px-6 box-border"
        >
          {t('apply')}
        </Button>
      </div>
    </div>
  );
}
